import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Tour {
  id: string;
  name: string;
  slug: string;
  main_image_url: string | null;
  gallery_images_urls: string[] | null;
  long_description: string | null;
  short_description: string | null;
  category_id: string | null;
  location: string | null;
  duration: string | null;
  max_capacity: number | null;
  features: string[] | null;
  highlights: string[] | null;
  not_suitable_for: string[] | null;
  includes: string[] | null;
  excludes: string[] | null;
  adult_price: number | null;
  child_price: number | null;
  schedules: string[] | null;
  pricing_type: 'individual' | 'package';
  base_price: number | null;
  is_active: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  tour_categories?: {
    name: string;
    icon: string;
  };
  tour_packages?: {
    id: string;
    package_name: string;
    package_price: number;
    persons_included: number;
    description: string | null;
    sort_order: number;
  }[];
}

export const useTours = () => {
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTours();
  }, []);

  const fetchTours = async () => {
    try {
      const { data, error } = await supabase
        .from('tours')
        .select(`
          *,
          tour_categories (
            name,
            icon
          ),
          tour_packages (
            id,
            package_name,
            package_price,
            persons_included,
            description,
            sort_order
          )
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching tours:', error);
        return;
      }

      setTours((data || []).map(tour => ({
        ...tour,
        pricing_type: (tour.pricing_type as 'individual' | 'package') || 'individual'
      })));
    } catch (error) {
      console.error('Error fetching tours:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFeaturedTours = () => {
    return tours.filter(tour => tour.is_featured);
  };

  const getTourBySlug = (slug: string) => {
    return tours.find(tour => tour.slug === slug);
  };

  return { 
    tours, 
    loading, 
    refetch: fetchTours, 
    getFeaturedTours, 
    getTourBySlug 
  };
};